import {
  LIBRARY_UNIT_LABELS,
  OBLIGATION_BASIS_LABELS,
  type LibraryUnit,
  type ObligationBasis,
  type ObligationKind,
} from "./library.js";
import {
  BUDGET_STATUS_LABELS,
  formatIntervalOf,
  itemPlanType,
  PLAN_TYPE_LABELS,
  type BudgetStatus,
  type OverrideAction,
  type PlanType,
  type WorkType,
} from "./mjop.js";
import { formatEuro } from "./platform.js";

/**
 * Wijzigingsgeschiedenis van de MJOP-planning (docs/ASSETS-MJOP.md, "Geschiedenis"): per uitvoering
 * en per post wat er veranderde, door wie, wanneer en waarom. De server bewaart momentopnames van
 * de velden hieronder en legt alleen het verschil vast.
 */
export const HISTORY_ENTITIES = ["asset_obligation", "budget_item"] as const;
export type HistoryEntity = (typeof HISTORY_ENTITIES)[number];
export const HISTORY_ACTIONS = ["created", "updated", "deleted", "override", "override_reset"] as const;
export type HistoryAction = (typeof HISTORY_ACTIONS)[number];

type Value = string | number | boolean | null;
export type HistorySnapshot = Record<string, Value>;
export interface HistoryChange {
  field: string;
  from: Value;
  to: Value;
}

/** Bewaard in `audit_log.data`. */
export interface HistoryData {
  buildingId: string;
  /** Naam van de regel op dat moment (blijft leesbaar na verwijderen). */
  label: string;
  changes?: HistoryChange[];
  year?: number;
  override?: { action: OverrideAction; newYear: number | null; amountCents: number | null };
  reason?: string | null;
}

export interface HistoryEntryDto extends HistoryData {
  id: string;
  at: string;
  action: HistoryAction;
  entity: HistoryEntity;
  entityId: string;
  /** Naam van wie het deed; null = onbekend of verwijderd. */
  by: string | null;
}
export interface HistoryResponse {
  entries: HistoryEntryDto[];
}

export function obligationSnapshot(o: {
  name: string;
  kind: ObligationKind;
  basis: ObligationBasis;
  intervalMonths: number | null;
  intervalWeeks: number | null;
  costCents: number | null;
  nextDueOn: string | null;
  lastPerformedOn: string | null;
  endYear: number | null;
  performer: string | null;
  notApplicable: boolean;
  overrideReason: string | null;
}): HistorySnapshot {
  return {
    name: o.name,
    type: o.kind,
    basis: o.basis,
    interval: formatIntervalOf(o.intervalMonths, o.intervalWeeks),
    costCents: o.costCents,
    nextDueOn: o.nextDueOn,
    lastPerformedOn: o.lastPerformedOn,
    endYear: o.endYear,
    performer: o.performer,
    notApplicable: o.notApplicable,
    overrideReason: o.overrideReason,
  };
}

export function itemSnapshot(i: {
  description: string;
  workType: WorkType;
  kind: ObligationKind | null;
  basis: ObligationBasis | null;
  firstYear: number;
  lastYear: number | null;
  cycleYears: number | null;
  unitPriceCents: number;
  quantity: number;
  unit: LibraryUnit;
  priceLevelYear: number | null;
  status: BudgetStatus;
  elementCode: string | null;
}): HistorySnapshot {
  return {
    name: i.description,
    type: itemPlanType(i),
    basis: i.basis,
    firstYear: i.firstYear,
    endYear: i.lastYear,
    interval: formatIntervalOf(i.cycleYears ? i.cycleYears * 12 : null),
    unitPriceCents: i.unitPriceCents,
    quantity: i.quantity,
    unit: i.unit,
    priceLevelYear: i.priceLevelYear,
    status: i.status,
    elementCode: i.elementCode,
  };
}

/** Velden die verschillen tussen twee momentopnames, in vaste volgorde. */
export function diffSnapshots(before: HistorySnapshot, after: HistorySnapshot): HistoryChange[] {
  return Object.keys(after)
    .filter((k) => (before[k] ?? null) !== (after[k] ?? null))
    .map((k) => ({ field: k, from: before[k] ?? null, to: after[k] ?? null }));
}

export const HISTORY_FIELD_LABELS: Record<string, string> = {
  name: "Omschrijving",
  type: "Type",
  basis: "Eis",
  interval: "Interval",
  costCents: "Per keer",
  unitPriceCents: "Prijs per eenheid",
  quantity: "Hoeveelheid",
  unit: "Eenheid",
  priceLevelYear: "Prijspeil",
  nextDueOn: "Volgende uitvoering",
  lastPerformedOn: "Laatste keer",
  firstYear: "Startjaar",
  endYear: "Stopjaar",
  performer: "Uitvoerder",
  notApplicable: "Niet van toepassing",
  overrideReason: "Reden niet van toepassing",
  status: "Status",
  elementCode: "NL-SfB",
};

const MONTHS = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const day = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m! - 1]} ${y}`;
};

/** Waarde zoals de gebruiker hem kent. */
export function historyValue(field: string, v: Value): string {
  if (v === null || v === "") {
    if (field === "endYear") return "doorlopend";
    if (field === "nextDueOn" || field === "lastPerformedOn") return "onbekend";
    return "leeg";
  }
  switch (field) {
    case "type":
      return PLAN_TYPE_LABELS[v as PlanType] ?? String(v);
    case "basis":
      return OBLIGATION_BASIS_LABELS[v as ObligationBasis] ?? String(v);
    case "costCents":
    case "unitPriceCents":
      return formatEuro(Number(v));
    case "unit":
      return LIBRARY_UNIT_LABELS[v as LibraryUnit] ?? String(v);
    case "status":
      return BUDGET_STATUS_LABELS[v as BudgetStatus] ?? String(v);
    case "nextDueOn":
    case "lastPerformedOn":
      return day(String(v));
    case "notApplicable":
      return v ? "ja" : "nee";
    case "quantity":
      return Number(v).toLocaleString("nl-NL");
    default:
      return String(v);
  }
}

/** Regels tekst bij een item, bv. "Interval: 2 jr → 1 jr" of "2028: verschoven naar 2029". */
export function historyLines(e: Pick<HistoryEntryDto, "action" | "changes" | "year" | "override">): string[] {
  switch (e.action) {
    case "created":
      return ["Aangemaakt"];
    case "deleted":
      return ["Verwijderd"];
    case "override_reset":
      return [`${e.year}: hersteld zoals gepland`];
    case "override": {
      const o = e.override;
      if (!o) return [`${e.year}: aangepast`];
      if (o.action === "skip") return [`${e.year}: overgeslagen`];
      if (o.action === "shift") return [`${e.year}: verschoven naar ${o.newYear}`];
      return [`${e.year}: bedrag ${o.amountCents != null ? formatEuro(o.amountCents) : "leeg"}`];
    }
    case "updated":
      return (e.changes ?? []).map(
        (c) =>
          `${HISTORY_FIELD_LABELS[c.field] ?? c.field}: ${historyValue(c.field, c.from)} → ${historyValue(c.field, c.to)}`,
      );
  }
}

/**
 * Leesbaarder overzicht (nieuwste eerst): opeenvolgende wijzigingen van dezelfde persoon aan dezelfde
 * regel binnen 10 minuten worden één regel (eerste "van", laatste "naar"). Aanmaken, per keer
 * aanpassen en een opgegeven reden blijven eigen regels.
 */
export function compactHistory(entries: HistoryEntryDto[], windowMs = 10 * 60_000): HistoryEntryDto[] {
  const out: HistoryEntryDto[] = [];
  // Van oud naar nieuw samenvoegen, daarna weer omdraaien.
  for (const e of [...entries].reverse()) {
    const prev = out[out.length - 1];
    const close =
      prev &&
      prev.entity === e.entity &&
      prev.entityId === e.entityId &&
      prev.by === e.by &&
      Date.parse(e.at) - Date.parse(prev.at) <= windowMs &&
      e.action === "updated" &&
      !e.reason;
    if (close && prev.action === "updated" && !prev.reason) {
      const merged = new Map((prev.changes ?? []).map((c) => [c.field, { ...c }]));
      for (const c of e.changes ?? []) {
        const m = merged.get(c.field);
        if (m) m.to = c.to;
        else merged.set(c.field, { ...c });
      }
      out[out.length - 1] = {
        ...prev,
        at: e.at,
        label: e.label,
        changes: [...merged.values()].filter((c) => c.from !== c.to),
      };
      continue;
    }
    out.push(e);
  }
  return out.filter((e) => e.action !== "updated" || (e.changes ?? []).length).reverse();
}
